<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedMarketOptions"
      multi-select
      searchable
      class="tw-min-w-[200px]"
      data-test="contacts-filter-markets"
      :label="forContacts ? $t('Market') : $t('Country')"
      :list="marketOptions"
      :placeholder="forContacts ? $t('All markets') : $t('All countries')"
      :max-labels-visible="5"
      can-deselect
      labels-primary
      :loading="loading"
    >
      <template #option="{ option }">
        <div class="tw-flex tw-items-center tw-gap-2">
          <WdsIcon :size="16" :name="computeFlagIcon(option.value.toUpperCase())" />
          {{ option.label }}
        </div>
      </template>
    </WdsSelect>

    <WdsSelect
      v-if="selectedMarketCodes.length > 0"
      v-model="selectedRegionOptions"
      multi-select
      searchable
      class="tw-min-w-[200px]"
      data-test="contacts-filter-regions"
      :label="$t('Regions')"
      :list="regionOptions"
      :placeholder="$t('All regions')"
      :max-labels-visible="5"
      can-deselect
      labels-primary
      :loading="loading"
    >
      <template #option="{ option }">
        <span class="single-line tw-flex tw-items-center tw-gap-2" :title="option.label">
          <WdsIcon
            v-if="option.marketCode"
            :size="16"
            :name="computeFlagIcon(`${option.marketCode}`.toUpperCase())"
          />
          {{ option.label }}
        </span>
      </template>
    </WdsSelect>
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsIcon, WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, inject } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { computeFlagIcon } from '@/utils/ts-tools'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { ContactAccounts } from '@/components/GraphqlDataTable/filters/trustedContacts/injection'

const { meta } = defineProps<{ meta: FilterMetaDataProp }>()

type RegionFilterModel = { markets: string[]; regionIds: number[] } | null

const model = defineModel<RegionFilterModel>({
  default: null,
})

const COMPANY_RECRUITER_REGIONS_QUERY = graphql(`
  query CompanyRecruiterRegionsFilterQuery {
    companyRecruiterRegions {
      code
      name
      regions {
        id
        name
      }
    }
  }
`)

const CLIENT_RELATION_REGIONS_QUERY = graphql(`
  query ClientRelationRegionsFilterQuery($accounts: [ID!]) {
    clientRelationRegions(accounts: $accounts) {
      code
      name
      regions {
        id
        name
      }
    }
  }
`)

const TRUSTED_CONTACT_REGIONS_QUERY = graphql(`
  query TrustedContactRegionsFilterQuery($accounts: [ID!]) {
    trustedContactRegions(accounts: $accounts) {
      code
      name
      regions {
        id
        name
      }
    }
  }
`)

// Each table offers the markets of its own rows: clients, trusted contacts or staffing agencies.
const forClients = meta.filter.inputArgName === 'clientRegions'
const forContacts = meta.filter.inputArgName === 'contactRegions'
const forRecruiters = !forClients && !forContacts

const { selectedAccounts } = useAccountsStore()

// The accounts store never holds staffing agency accounts, so they fall back to the table's own scope.
const accountsVariables = () => ({
  accounts:
    selectedAccounts.length === 0
      ? [currentAccount.globalId]
      : selectedAccounts.map((account) => account.id),
})

const { result: recruiterResult, loading: recruiterLoading } = useQuery(
  COMPANY_RECRUITER_REGIONS_QUERY,
  null,
  () => ({ clientId: 'publicApi', fetchPolicy: 'cache-and-network', enabled: forRecruiters }),
)

// The group contacts tab lists sibling divisions' contacts, so its markets come from the table's own accounts.
const contactAccounts = inject(ContactAccounts, null)

const { result: contactResult, loading: contactLoading } = useQuery(
  TRUSTED_CONTACT_REGIONS_QUERY,
  () => (contactAccounts ? { accounts: contactAccounts.value } : accountsVariables()),
  () => ({ clientId: 'publicApi', fetchPolicy: 'cache-and-network', enabled: forContacts }),
)

const { result: clientResult, loading: clientLoading } = useQuery(
  CLIENT_RELATION_REGIONS_QUERY,
  accountsVariables,
  () => ({ clientId: 'publicApi', fetchPolicy: 'cache-and-network', enabled: forClients }),
)

const loading = computed(() =>
  forClients ? clientLoading.value : forContacts ? contactLoading.value : recruiterLoading.value,
)

const marketsFromQuery = computed(
  () =>
    (forClients
      ? clientResult.value?.clientRelationRegions
      : forContacts
        ? contactResult.value?.trustedContactRegions
        : recruiterResult.value?.companyRecruiterRegions) ?? [],
)

const marketOptions = computed(() =>
  marketsFromQuery.value.map((market) => ({
    label: market.name,
    value: market.code,
  })),
)

const selectedMarketCodes = computed<string[]>(() => model.value?.markets ?? [])
const selectedRegionIds = computed<number[]>(() => model.value?.regionIds ?? [])

const regionsFor = (marketCodes: string[]) =>
  new Set(
    marketsFromQuery.value
      .filter((market) => marketCodes.includes(market.code))
      .flatMap((market) => market.regions.map((region) => region.id)),
  )

// Derived from the persisted model: a shadow ref plus a lazy watch left both selects empty on reload while the table filtered.
const selectedMarketOptions = computed({
  get: () => marketOptions.value.filter((option) => selectedMarketCodes.value.includes(option.value)),
  set: (options: Array<{ value: string }>) => {
    const markets = options.map((option) => option.value)

    if (markets.length === 0) {
      model.value = null
      return
    }

    // Regions belonging to a market that just went away go with it.
    const valid = regionsFor(markets)

    model.value = {
      markets,
      regionIds: selectedRegionIds.value.filter((id) => valid.has(id)),
    }
  },
})

const regionOptions = computed(() => {
  const options: Array<{
    label: string
    value: number | string
    id?: string
    marketCode?: string
  }> = []
  const selected = selectedMarketCodes.value
  const matchingMarkets = marketsFromQuery.value.filter((m) => selected.includes(m.code))

  for (const market of matchingMarkets) {
    for (const region of market.regions) {
      options.push({
        label: region.name,
        marketCode: market.code,
        value: region.id,
      })
    }
  }

  return options
})

const selectedRegionOptions = computed({
  get: () => regionOptions.value.filter((option) => selectedRegionIds.value.includes(option.value as number)),
  set: (options: Array<{ value: number | string }>) => {
    model.value = {
      markets: selectedMarketCodes.value,
      regionIds: options.map((option) => Number(option.value)),
    }
  },
})
</script>

<style lang="scss" scoped>
.single-line {
  @apply tw-break-keep tw-text-ellipsis tw-whitespace-nowrap tw-overflow-hidden tw-max-w-full;
}
</style>
